import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  Typography,
  CircularProgress,
  Switch,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import axios from 'axios';

type SubjectType = 'THEORY' | 'PRACTICAL' | 'BOTH';

interface SubjectMasterModel {
  id: string;
  name: string;
  code: string;
  subjectType: SubjectType;
  isActive: boolean;
}

const GlobalSubjectMaster: React.FC = () => {
  const [items, setItems] = useState<SubjectMasterModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<SubjectMasterModel | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    subjectType: 'THEORY' as SubjectType,
  });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const res = await axios.get('/system-settings/masters/subjectMaster');
      setItems(res.data);
    } catch (err) {
      console.error('Failed to fetch subjects', err);
      setError('Failed to fetch subjects');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingItem(null);
    setFormData({
      name: '',
      code: '',
      subjectType: 'THEORY',
    });
    setDialogOpen(true);
  };

  const handleEdit = (item: SubjectMasterModel) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      code: item.code,
      subjectType: item.subjectType,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this subject?')) return;
    try {
      await axios.delete(`/system-settings/masters/subjectMaster/${id}`);
      fetchItems();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to delete subject');
    }
  };

  const handleSave = async () => {
    try {
      if (editingItem) {
        await axios.patch(`/system-settings/masters/subjectMaster/${editingItem.id}`, formData);
      } else {
        await axios.post('/system-settings/masters/subjectMaster', formData);
      }
      setDialogOpen(false);
      fetchItems();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to save subject');
    }
  };

  const handleToggleStatus = async (id: string, currentStatus: boolean) => {
    try {
      await axios.patch(`/system-settings/masters/subjectMaster/${id}/toggle-status`);
      setItems((prev) =>
        prev.map((item) =>
          item.id === id ? { ...item, isActive: !currentStatus } : item
        )
      );
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to toggle status');
      fetchItems();
    }
  };

  if (loading) {
    return <CircularProgress />;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Global Subject Master</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>
          New Subject
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Code</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No subjects found.
                </TableCell>
              </TableRow>
            ) : (
              items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{item.name}</TableCell>
                  <TableCell>{item.code}</TableCell>
                  <TableCell>{item.subjectType}</TableCell>
                  <TableCell>
                    <Switch
                      checked={item.isActive}
                      onChange={() => handleToggleStatus(item.id, item.isActive)}
                      color="primary"
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => handleEdit(item)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(item.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>{editingItem ? 'Edit Subject' : 'New Subject'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1, minWidth: 320 }}>
            <TextField
              label="Subject Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Code"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value })}
              fullWidth
              required
            />
            <TextField
              select
              label="Subject Type"
              value={formData.subjectType}
              onChange={(e) => setFormData({ ...formData, subjectType: e.target.value as SubjectType })}
              fullWidth
              required
            >
              <MenuItem value="THEORY">Theory</MenuItem>
              <MenuItem value="PRACTICAL">Practical</MenuItem>
              <MenuItem value="BOTH">Both</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GlobalSubjectMaster;
